/** Cliente HTTP del módulo de Compras / SIC (Fase 3). Espejo de app/schemas/compras.py. */
import { apiRequest } from "../../lib/api";

// ── Configuración (política) ──
export type ModoCompras = "Directo" | "ConSIC";
export type PoliticaSinAprobador = "Autoaprobar" | "Bloquear";

export interface ConfiguracionComprasRead {
  modo: ModoCompras;
  umbral_sic: number;
  umbral_aprobacion: number;
  permite_autoaprobacion: boolean;
  politica_sin_aprobador: PoliticaSinAprobador;
  rol_aprobador: string | null;
  aprobador_usuario_id: number | null;
  /** Cuánto puede exceder una OC el monto aprobado en su SIC (%). */
  tolerancia_oc_pct: number;
}
export type ConfiguracionComprasUpdate = Partial<ConfiguracionComprasRead>;

export interface DecisionCompra {
  requiere_sic: boolean;
  requiere_aprobacion: boolean;
  puede_autoaprobar: boolean;
  ratificacion_pendiente: boolean;
  aprobador_usuario_id: number | null;
  resumen: string;
}

// ── SIC ──
export type EstadoSIC =
  | "Borrador"
  | "Solicitada"
  | "Aprobada"
  | "Rechazada"
  | "Comprada"
  | "Anulada";
export type PrioridadSIC = "Normal" | "Urgente" | "Emergencia";

export interface DetalleSICRead {
  id: number;
  insumo_id: number | null;
  descripcion: string;
  cantidad: number | string;
  costo_estimado_unitario: number;
  costo_estimado_total: number;
}
export interface DetalleSICIn {
  descripcion: string;
  cantidad: number;
  insumo_id?: number | null;
  costo_estimado_unitario?: number;
}

export interface SolicitudCompraRead {
  id: number;
  anio: number;
  numero: number;
  estado: EstadoSIC;
  prioridad: PrioridadSIC;
  solicitante_id: number | null;
  justificacion: string;
  bodega_destino_id: number | null;
  ot_id: number | null;
  reunion_id: number | null;
  monto_estimado: number;
  requirio_aprobacion: boolean;
  ratificacion_pendiente: boolean;
  aprobada_por_id: number | null;
  fecha_aprobacion: string | null;
  motivo_rechazo: string | null;
  ratificada_por_id: number | null;
  fecha_ratificacion: string | null;
  detalles: DetalleSICRead[];
}
export interface SolicitudCompraCreate {
  justificacion: string;
  prioridad?: PrioridadSIC;
  bodega_destino_id?: number | null;
  detalles: DetalleSICIn[];
}

// ── OC ──
export type EstadoOC = "Emitida" | "RecibidaParcial" | "Recibida" | "Anulada";

export interface DetalleOCRead {
  id: number;
  insumo_id: number | null;
  descripcion: string;
  cantidad: number | string;
  costo_unitario: number;
  costo_total: number;
  cantidad_recibida: number | string;
  es_medidor: boolean;
}
export interface DetalleOCIn {
  descripcion: string;
  cantidad: number;
  costo_unitario: number;
  insumo_id?: number | null;
  es_medidor?: boolean;
}

export interface DetalleRecepcionRead {
  insumo_id: number;
  detalle_oc_id: number | null;
  cantidad: number | string;
  costo_unitario: number;
  costo_total: number;
}
/** Movimiento ENTRADA que recepcionó la OC (id = movimiento_id). */
export interface RecepcionRead {
  id: number;
  fecha: string;
  estado: "Confirmado" | "Anulado";
  bodega_destino_id: number | null;
  documento_tipo: string | null;
  numero_documento: string | null;
  detalles: DetalleRecepcionRead[];
}

export interface OrdenCompraRead {
  id: number;
  anio: number;
  numero: number;
  solicitud_id: number | null;
  proveedor_id: number;
  bodega_destino_id: number | null;
  estado: EstadoOC;
  fecha_emision: string;
  condiciones_pago: string | null;
  observacion: string | null;
  monto_total: number;
  detalles: DetalleOCRead[];
  recepciones: RecepcionRead[];
}
export interface OrdenCompraCreate {
  proveedor_id: number;
  solicitud_id?: number | null;
  bodega_destino_id?: number | null;
  fecha_emision?: string | null;
  condiciones_pago?: string | null;
  observacion?: string | null;
  detalles: DetalleOCIn[];
}
export interface LineaRecepcion {
  detalle_id: number;
  cantidad: number;
}
export interface LineaRecepcionMedidor {
  detalle_id: number;
  series: string[];
  marca?: string | null;
  diametro?: string | null;
  tipo?: string | null;
}
export interface RecibirOCBody {
  bodega_id?: number | null;
  /** Documento del proveedor que respalda la recepción (factura / guía). */
  documento_tipo?: string | null;
  numero_documento?: string | null;
  lineas?: LineaRecepcion[];
  lineas_medidor?: LineaRecepcionMedidor[];
}

const B = "/api/v1/compras";

// Config
export const verConfiguracion = (): Promise<ConfiguracionComprasRead> =>
  apiRequest(`${B}/configuracion`);
export const actualizarConfiguracion = (
  p: ConfiguracionComprasUpdate,
): Promise<ConfiguracionComprasRead> =>
  apiRequest(`${B}/configuracion`, { method: "PATCH", body: p });

// SIC
export const evaluarPolitica = (body: {
  monto: number;
  prioridad?: PrioridadSIC;
  categoria_ids?: number[];
}): Promise<DecisionCompra> =>
  apiRequest(`${B}/solicitudes/evaluar`, { method: "POST", body });
export const listarSolicitudes = (
  estado?: EstadoSIC,
  ratificacionPendiente?: boolean,
): Promise<SolicitudCompraRead[]> => {
  const qs = new URLSearchParams();
  if (estado) qs.set("estado", estado);
  if (ratificacionPendiente !== undefined) {
    qs.set("ratificacion_pendiente", String(ratificacionPendiente));
  }
  const s = qs.toString();
  return apiRequest(`${B}/solicitudes${s ? `?${s}` : ""}`);
};
export const crearSolicitud = (p: SolicitudCompraCreate): Promise<SolicitudCompraRead> =>
  apiRequest(`${B}/solicitudes`, { method: "POST", body: p });
export const obtenerSolicitud = (id: number): Promise<SolicitudCompraRead> =>
  apiRequest(`${B}/solicitudes/${id}`);
export const enviarSolicitud = (id: number): Promise<SolicitudCompraRead> =>
  apiRequest(`${B}/solicitudes/${id}/enviar`, { method: "POST" });
export const aprobarSolicitud = (id: number): Promise<SolicitudCompraRead> =>
  apiRequest(`${B}/solicitudes/${id}/aprobar`, { method: "POST" });
export const rechazarSolicitud = (id: number, motivo: string): Promise<SolicitudCompraRead> =>
  apiRequest(`${B}/solicitudes/${id}/rechazar`, { method: "POST", body: { motivo } });
export const anularSolicitud = (id: number): Promise<SolicitudCompraRead> =>
  apiRequest(`${B}/solicitudes/${id}/anular`, { method: "POST" });
/** La directiva ratifica una SIC autoaprobada por falta de aprobador. */
export const ratificarSolicitud = (id: number): Promise<SolicitudCompraRead> =>
  apiRequest(`${B}/solicitudes/${id}/ratificar`, { method: "POST" });

// OC
export const listarOrdenesCompra = (estado?: EstadoOC): Promise<OrdenCompraRead[]> =>
  apiRequest(`${B}/ordenes-compra${estado ? `?estado=${estado}` : ""}`);
export const crearOrdenCompra = (p: OrdenCompraCreate): Promise<OrdenCompraRead> =>
  apiRequest(`${B}/ordenes-compra`, { method: "POST", body: p });
export const obtenerOrdenCompra = (id: number): Promise<OrdenCompraRead> =>
  apiRequest(`${B}/ordenes-compra/${id}`);
export const recibirOrdenCompra = (
  id: number,
  body: RecibirOCBody,
): Promise<OrdenCompraRead> =>
  apiRequest(`${B}/ordenes-compra/${id}/recibir`, { method: "POST", body });
export const anularOrdenCompra = (id: number): Promise<OrdenCompraRead> =>
  apiRequest(`${B}/ordenes-compra/${id}/anular`, { method: "POST" });
/** Deshace una recepción de stock: reversa del movimiento y libera lo recibido. */
export const anularRecepcion = (
  id: number,
  movimientoId: number,
  motivo: string,
): Promise<OrdenCompraRead> =>
  apiRequest(`${B}/ordenes-compra/${id}/recepciones/${movimientoId}/anular`, {
    method: "POST",
    body: { motivo },
  });
